import { assertNever } from "@homebound/activesupport";
import { FloorPlan, InsideMapsIFrame, SelectedOptionCodesMap } from "~components";
import { Css } from "~generated/css";
import { isDefined, kebabCase, useTestIds } from "~utils";
import { ImageDisplayImageProps, resolveOptionCodesForImage } from "./ImageDisplay";

const IMG_PLACEHOLDER = "/Placeholder-Image-tile.png";

type PreviewCardProps = {
  image: ImageDisplayImageProps;
  onClick: () => void;
  isActive?: boolean;
  selectedOptionCodes?: SelectedOptionCodesMap;
  hideImage?: boolean;
  isReversed?: boolean;
};

// Configurator Preview Card
export function PreviewCard(props: PreviewCardProps) {
  const { image, onClick, isActive = false, selectedOptionCodes, hideImage = false, isReversed = false } = props;
  const tid = useTestIds(props, "preview-card");

  return (
    <button
      onClick={() => onClick()}
      css={Css.relative.z0.df.fdc.wPx(128).hPx(105).fs0.cursorPointer.bshBasic.br4.oh.add("transition", "0.2s").$}
      {...tid}
    >
      {/* Highlight Bar */}
      {isActive && (
        <div css={Css.absolute.z1.left0.right0.top0.hPx(4).bgYellow400.add("borderRadius", "4px 4px 0 0").$} />
      )}
      {/* Image Container */}
      <PreviewContent
        image={image}
        hideImage={hideImage}
        selectedOptionCodes={selectedOptionCodes}
        isReversed={isReversed}
      />
      {/* Text Container */}
      <div
        css={
          Css.bgWhite.w100.df.p1.aic.ttc.jcc
            .hPx(33)
            .if(hideImage)
            .br4.else.add("borderBottomLeftRadius", "inherit")
            .add("borderBottomRightRadius", "inherit").$
        }
      >
        {image.previewCardName && <span css={Css.add("transition", "0.2s").t12.$}>{image.previewCardName}</span>}
      </div>
    </button>
  );
}

function PreviewContent(props: {
  image: ImageDisplayImageProps;
  hideImage: boolean;
  selectedOptionCodes?: SelectedOptionCodesMap;
  isReversed: boolean;
}) {
  const { image, hideImage, selectedOptionCodes, isReversed } = props;
  if (hideImage) return;
  if (image.kind === "floorPlan") {
    if (!isDefined(image.imageUrl)) return null;
    return (
      <div css={Css.w100.fg1.bgOffWhite.$}>
        <FloorPlan
          id={image.id.replaceAll(":", "")}
          key={image.id}
          src={image.imageUrl}
          xss={Css.w100.h100.ar("16 / 9").$}
          maskHexColor="#F6F6F0"
          selectedOptionCodes={resolveOptionCodesForImage(image, selectedOptionCodes)}
          isReversed={isReversed}
          isPreview={true}
        />
      </div>
    );
  } else if (image.kind === "image") {
    return (
      <img
        css={
          Css.w100
            .ar("16 / 9")
            .add("borderRadius", "4px 4px 0 0")
            .objectCover.if(isReversed)
            .add("transform", "scaleX(-1)").$
        }
        src={isDefined(image.imageUrl) ? image.imageUrl : IMG_PLACEHOLDER}
        alt={`${image.name} Preview`}
      ></img>
    );
  } else if (image.kind === "insideMaps") {
    return (
      <InsideMapsIFrame
        iframeId={`${kebabCase(image.previewCardName ?? "")}-preview`}
        roomLocationId={image.roomLocationId}
        isPreview={true}
      />
    );
  } else {
    return assertNever(image);
  }
}
